// @flow
import React, { PureComponent } from 'react';
import { Redirect, withRouter } from 'react-router-dom';

import { withHelmet } from 'infrastructure/seo/Helmet';
import { getPublicAssets } from 'utils/assetsUtils';
import { getJsDate } from 'utils/dateUtils';
import { getMetaBySlug } from 'utils/seoUtils';

import TeamFilter from 'containers/team/TeamFilterContainer';
import CardNextMatch from 'components/fragments/card/CardNextMatch';
import Loading from 'components/fragments/Loading';

import type { PageListType } from 'types/PageList';
import type { Status } from 'types/Status';
import type { ResultCalendarContainerType, ResultCalendarType } from 'types/Result';
import type { RouterProps } from 'types/Router';
import type { CompetitionResultType } from 'types/Result';

import {
  STATUS_FAILURE,
  STATUS_DEFAULT,
  STATUS_LOADING,
} from 'constants/statusConstants';

export type StateProps = {
  title: string,
  nextMatches: ResultCalendarContainerType,
  status: Status,
  competitions_ovale: number[],
  competitions: CompetitionResultType[],
  clubName: string,
  rejoignez_nous: string,
  rejoignez_nous_background: string,
  currentSeason: { id: string, nom: string },
  historique: $PropertyType<PageListType, 'competitions_saisons'>,
};

type Props = StateProps & RouterProps;

type State = {
  monthsToShow: number,
  totalMonths: number,
  selectedCompetitions: number[],
  stateDerivedFromProps: boolean,
};

class TeamCalendar extends PureComponent<Props, State> {
  state: State = {
    monthsToShow: 0,
    totalMonths: 0,
    selectedCompetitions: [],
    stateDerivedFromProps: false,
  };

  static getDerivedStateFromProps(nextProps: Props, prevState: State) {
    const { totalMonths, selectedCompetitions, stateDerivedFromProps } = prevState;
    const { nextMatches, competitions_ovale, currentSeason, historique } = nextProps;

    const totalEvents = Object.keys(nextMatches).length;

    if (nextMatches && !stateDerivedFromProps && selectedCompetitions.length < 1 && competitions_ovale.length > 0) {
      if (totalEvents > totalMonths) {
        return {
          totalMonths: totalEvents,
          monthsToShow: Math.min(totalEvents, 2),
          selectedCompetitions: competitions_ovale,
          stateDerivedFromProps: true,
        };
      }
    } else if (nextMatches && !stateDerivedFromProps && 
        selectedCompetitions.length < 1 && 
        historique[currentSeason.id] && 
        historique[currentSeason.id].competitions && 
        historique[currentSeason.id].competitions.length > 0) {
      if (totalEvents > totalMonths) {
        let competitions = historique[currentSeason.id].competitions || [];
        return {
          totalMonths: totalEvents,
          monthsToShow: Math.min(totalEvents, 2),
          selectedCompetitions: competitions.map(({ id }) => parseInt(id, 10)),
          stateDerivedFromProps: true,
        };
      }
    }
    return null;
  }

  _renderMatch = (results: ResultCalendarType[]): any[] => {
    const { match: { params: { slug } } } = this.props;
    const { selectedCompetitions } = this.state;

    return results
      .filter(({ competitionId: id }) => selectedCompetitions.includes(parseInt(id, 10)))
      .sort((a, b) => {
        const aDateEffective = a.dateEffective;
        const bDateEffective = b.dateEffective;

        return aDateEffective < bDateEffective ? -1 : aDateEffective > bDateEffective ? 1 : 0;
      })
      .map((result: ResultCalendarType): any => (
        <CardNextMatch key={result.id} result={result} teamSlug={slug || ''} />
      ));
  };

  _renderMoreEvents = () => {
    const { monthsToShow, totalMonths, selectedCompetitions } = this.state;

    if (selectedCompetitions.length < 1 || monthsToShow > totalMonths) {
      return (
        <>
          <div className="row mt-5"></div>
          <div className="row mt-5"></div>
          <div className="row mt-5"></div>
        </>
      );
    }
    return (
      <a // eslint-disable-line
        className="btn btn--see-more btn--primary"
        onClick={() => this.setState((prevState) => ({ monthsToShow: prevState.monthsToShow + 2 }))}
      >
        Voir plus d&apos;évènements
      </a>
    );
  };

  _renderMonths = () => {
    const { monthsToShow, selectedCompetitions } = this.state;
    const { nextMatches } = this.props;

    // we have to sort months according to the date
    const months = Object.keys(nextMatches);
    months.sort((stringDateA, stringDateB) => {
      const [monthA, yearA] = stringDateA.split(' ');
      const [monthB, yearB] = stringDateB.split(' ');

      const jsDateA = getJsDate(monthA, yearA);
      const jsDateB = getJsDate(monthB, yearB);

      return jsDateA < jsDateB ? -1 : 1;
    });

    if (selectedCompetitions.length < 1 || months.length < 1) {
      return (
        <div className="placeholder col col-lg-8on9">
          <p className="ft-h5">
            <i className="icon " />
            Aucun évènement pour cette recherche
          </p>
        </div>
      );
    }

    return months.slice(0, monthsToShow).map<React$Element<'div'>>((month) => (
      <div key={month.replace(' ', '_')} className="row mt-5">
        <div className="col col-lg-8on9">
          <h3 className="mb-3">{month}</h3>
          <>
            {this._renderMatch(nextMatches[month])}
          </>
        </div>
      </div>
    ));
  };

  handleCompetitionChange = (selectedCompetitions: number[]) => {
    this.setState({ selectedCompetitions });
  };

  render() {
    const {
      title,
      status,
      clubName,
      rejoignez_nous,
      rejoignez_nous_background,
      match: { url },
      historique,
      currentSeason,
    } = this.props;
    const { selectedCompetitions } = this.state;

    if (status === STATUS_DEFAULT || status === STATUS_LOADING) {
      return <Loading />;
    }

    if (status === STATUS_FAILURE) {
      return <Redirect to={url.replace('/calendrier', '')} />;
    }

    const data = historique[currentSeason.id];
    const background = rejoignez_nous_background ? rejoignez_nous_background : getPublicAssets('/img/trash/card.jpg');

    return (
      <div className="container">
        <div className="row">
          <div className="col col-md-8 col-lg-9 bg-overflow no-padding">
            <h2 className='mt-3'>{`Calendrier de l'équipe ${title} de ${clubName}`}</h2>
            <section>
              <TeamFilter
                className="header-filter header-filter--team header-filter--teamWfilter mt-2 mt-lg-8"
                // $FlowFixMe
                competitions={data?.competitions ?? []}
                onCompetitionChange={this.handleCompetitionChange}
                selectedCompetitions={selectedCompetitions}
                withLink
                withCompetition />
              <div className="events not-relative">
                {this._renderMonths()}
                {this._renderMoreEvents()}
              </div>
            </section>
          </div>
          <div className="col col-md-4 col-lg-3">
            <aside className="sidebar">
              {rejoignez_nous &&
                <a
                  href={rejoignez_nous}
                  title="Voir les modalités d'inscription"
                  className="box card card--content mb-4"
                >
                  <div
                    className="card__thumbnail img-blue"
                    style={{ backgroundImage: `url(${background})` }}
                  >
                    <img src={background} alt='' />
                  </div>
                  <div className="card__body">
                    <h3 className="card__title">Rejoignez-nous&nbsp;!</h3>
                    <p className="card__desc"></p>
                  </div>
                  <div className="card__footer">
                    <span className="link-chevron ">
                      Consulter
                      <i className="icon icon-chevron-right" />
                    </span>
                  </div>
                </a>}
            </aside>
          </div>
        </div>
      </div>
    );
  }
}

export default withHelmet(withRouter(TeamCalendar))((Helmet, pageProps) => {
  const { title, clubName, match: { params, url } } = pageProps;
  const meta = getMetaBySlug(url, params);
  const ogimage = pageProps.featured_media ? pageProps.featured_media.src : false;
  const ogimagetitle = pageProps.featured_media ? pageProps.featured_media.title : false;

  return (
    <Helmet>
      <title>{meta.title(title, clubName)}</title>
      {
        meta.description()
          ? <meta name={'description'} content={meta.description(title)} />
          : ''
      }
      <meta name={'keywords'} content={meta.keywords(title, clubName)} />
      <meta property={'og:url'} content={window.location.href} />
      <meta property={'og:title'} content={meta.title(title, clubName)} />
      {
        meta.description()
          ? <meta property={'og:description'} content={meta.description(title)} />
          : ''
      }
      {ogimage && <meta property={'og:image'} content={ogimage} />}
      {ogimage && <meta property={'og:image:url'} content={ogimage} />}
      {(ogimage && ogimagetitle) && <meta property={'og:image:title'} content={ogimagetitle} />}
      <link rel={'canonical'} href={window.location.href} />
    </Helmet>
  );
});